import React, { useEffect } from 'react';
import * as signalR from "@microsoft/signalr";
import { AlertColor } from '@mui/material';

const Emitter = require("tiny-emitter/instance");

// How long a tab may stay hidden before its connection is dropped, so quick tab
// switches don't tear down and rebuild the socket.
const HIDDEN_GRACE_MS = 45_000;

const SEVERITIES: AlertColor[] = ['success', 'info', 'warning', 'error'];

const onReceiveNotification = (message: string, severity: string) => {
    const alertSeverity: AlertColor = SEVERITIES.includes(severity as AlertColor)
        ? severity as AlertColor
        : 'info';
    Emitter.emit('notify', message, alertSeverity);
};

// Holds a notification hub connection only while the tab is visible (plus a grace
// period after it's hidden). Broadcasts are rare, so an idle background tab
// shouldn't keep a socket open for hours.
const NotificationListener: React.FC = () => {
    useEffect(() => {
        const connection = new signalR.HubConnectionBuilder()
            // Straight to WebSockets: no negotiate round trip, and no connection id that
            // goes stale (404) after a server restart or idle period.
            .withUrl("/notificationHub", {
                skipNegotiation: true,
                transport: signalR.HttpTransportType.WebSockets,
            })
            .withAutomaticReconnect()
            .build();

        // Registered once per connection object, so restarts don't add duplicate handlers.
        connection.on('ReceiveNotification', onReceiveNotification);

        let disposed = false;
        let stopTimer: ReturnType<typeof setTimeout> | undefined;
        // start()/stop() calls are chained so they never overlap; each sync re-reads the
        // desired state when it runs, so the last visibility change always wins.
        let pending: Promise<void> = Promise.resolve();

        const shouldBeConnected = () => !disposed && document.visibilityState === 'visible';

        const sync = () => {
            pending = pending.then(async () => {
                try {
                    if (shouldBeConnected()) {
                        // Connecting/Reconnecting/Connected are left alone, so an in-progress
                        // automatic reconnect is never double-started.
                        if (connection.state === signalR.HubConnectionState.Disconnected) {
                            await connection.start();
                        }
                    } else if (connection.state !== signalR.HubConnectionState.Disconnected) {
                        // A deliberate stop() also cancels any automatic reconnect loop;
                        // auto-reconnect only kicks in after an unexpected drop.
                        await connection.stop();
                    }
                } catch (e) {
                    console.log('Notification hub connection failed: ', e);
                }
            });
        };

        const clearStopTimer = () => {
            if (stopTimer !== undefined) {
                clearTimeout(stopTimer);
                stopTimer = undefined;
            }
        };

        const onVisibilityChange = () => {
            clearStopTimer();
            if (document.visibilityState === 'visible') {
                sync();
            } else {
                stopTimer = setTimeout(() => {
                    stopTimer = undefined;
                    sync();
                }, HIDDEN_GRACE_MS);
            }
        };

        document.addEventListener('visibilitychange', onVisibilityChange);
        // A tab opened in the background waits until it's first shown.
        sync();

        return () => {
            disposed = true;
            clearStopTimer();
            document.removeEventListener('visibilitychange', onVisibilityChange);
            connection.off('ReceiveNotification', onReceiveNotification);
            sync(); // stops the connection once any in-flight start() settles
        };
    }, []);

    return null; // This component does not render anything directly
};

export default NotificationListener;
